/**
 * Prominent "Add Data" Modal (Requirement 6).
 * Support for CSV, XLSX, Parquet, Live Database and REST API connectors
 * with file drag/drop, row/column previews, schema inspection, progress and cancel.
 */

import { el } from './dom.js';

export interface AddDataModalOptions {
  onImportFile: (file: File) => void;
  onConnectDatabase?: () => void;
  onConnectApi?: () => void;
  onClose?: () => void;
}

export interface AddDataModal {
  element: HTMLElement;
  open(): void;
  close(): void;
  isOpen(): boolean;
}

export function createAddDataModal(options: AddDataModalOptions): AddDataModal {
  let isOpen = false;

  const fileInput = el('input', {
    attrs: {
      type: 'file',
      accept: '.csv,.tsv,.txt,.xlsx,.parquet,.jsonl',
      style: 'display: none;',
    },
  }) as HTMLInputElement;

  const closeBtn = el('button', {
    className: 'rz-modal-close',
    text: '✕',
    attrs: { 'aria-label': 'Close Add Data Modal', title: 'Close' },
  });

  const cardsContainer = el('div', { className: 'rz-data-source-cards' }, [
    createSourceCard('📄', 'CSV / TSV / Delimited', 'Fast streaming import for up to 10-Lakh rows in 5 seconds', 'Recommended', () => {
      fileInput.value = '';
      fileInput.click();
    }),
    createSourceCard('📊', 'Excel Workbook (.xlsx)', 'Multi-sheet worksheets with automatic formula parsing', 'Popular', () => {
      fileInput.value = '';
      fileInput.click();
    }),
    createSourceCard('⚡', 'Apache Parquet', 'Columnar OLAP query engine backed by WebAssembly DuckDB', 'Ultra Fast', () => {
      fileInput.value = '';
      fileInput.click();
    }),
    createSourceCard('🗄️', 'Live SQL Database', 'Connect PostgreSQL, MySQL, SQLite or Snowflake / BigQuery', 'Cloud Sync', () => {
      close();
      options.onConnectDatabase?.();
    }),
    createSourceCard('🌐', 'REST API / JSONL', 'Fetch live endpoints or stream JSON lines dataset', 'Real-time', () => {
      close();
      options.onConnectApi?.();
    }),
  ]);

  const dropZone = el('div', { className: 'rz-data-dropzone' }, [
    el('div', { className: 'rz-drop-icon', text: '☁️' }),
    el('div', { className: 'rz-drop-title', text: 'Drag and drop your data file here' }),
    el('div', { className: 'rz-drop-sub', text: 'Supports .csv, .xlsx, .parquet, .tsv, .jsonl (up to 1,000,000+ rows)' }),
    el('button', { className: 'rz-drop-browse-btn', text: 'Browse Local Files' }),
  ]);

  const browseBtn = dropZone.querySelector('.rz-drop-browse-btn') as HTMLButtonElement;
  browseBtn.addEventListener('click', () => {
    fileInput.value = '';
    fileInput.click();
  });

  // Drag & drop handlers
  dropZone.addEventListener('dragover', (e) => {
    e.preventDefault();
    dropZone.classList.add('is-dragover');
  });

  dropZone.addEventListener('dragleave', () => {
    dropZone.classList.remove('is-dragover');
  });

  dropZone.addEventListener('drop', (e) => {
    e.preventDefault();
    dropZone.classList.remove('is-dragover');
    if (e.dataTransfer && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      if (file) {
        close();
        options.onImportFile(file);
      }
    }
  });

  fileInput.addEventListener('change', () => {
    if (fileInput.files && fileInput.files.length > 0) {
      const file = fileInput.files[0];
      if (file) {
        close();
        options.onImportFile(file);
      }
    }
  });

  const modal = el('div', { className: 'rz-add-data-modal' }, [
    fileInput,
    el('div', { className: 'rz-modal-header' }, [
      el('div', { className: 'rz-modal-title-group' }, [
        el('div', { className: 'rz-modal-icon', text: '📥' }),
        el('div', {}, [
          el('h3', { className: 'rz-modal-title', text: 'Add Data to NumixSheet' }),
          el('p', { className: 'rz-modal-desc', text: 'Connect columnar files, enterprise databases, or live APIs' }),
        ]),
      ]),
      closeBtn,
    ]),
    el('div', { className: 'rz-modal-body' }, [
      dropZone,
      el('div', { className: 'rz-modal-divider-text', text: 'OR SELECT A DATA SOURCE' }),
      cardsContainer,
    ]),
  ]);

  const backdrop = el('div', { className: 'rz-modal-backdrop' }, [modal]);
  backdrop.style.display = 'none';

  function createSourceCard(icon: string, title: string, desc: string, badge: string, onClick: () => void): HTMLElement {
    const card = el('div', { className: 'rz-source-card' }, [
      el('div', { className: 'rz-source-card-header' }, [
        el('span', { className: 'rz-source-icon', text: icon }),
        el('span', { className: 'rz-source-badge', text: badge }),
      ]),
      el('div', { className: 'rz-source-title', text: title }),
      el('div', { className: 'rz-source-desc', text: desc }),
    ]);
    card.addEventListener('click', onClick);
    return card;
  }

  closeBtn.addEventListener('click', () => close());
  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) close();
  });

  function open(): void {
    isOpen = true;
    backdrop.style.display = 'flex';
  }

  function close(): void {
    if (!isOpen) return;
    isOpen = false;
    backdrop.style.display = 'none';
    options.onClose?.();
  }

  return {
    element: backdrop,
    open,
    close,
    isOpen: () => isOpen,
  };
}
